// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — the pure game state. Immutable transitions (place / tick) so the whole
// thing is trivially testable and the Race can drive it without a DOM or a clock.
//
// A round: letters arrive from the deterministic stream; the player drops each into an
// empty cell. In Sluice a QUEUE of the next few letters is visible and any of them may
// be placed. The round ends when the grid is full — or, in versus, when the round
// timer expires (scoring whatever is placed; contiguous words still count).

import { buildStream } from './seed';
import { scoreGrid, type GridScore } from './score';
import { cellCount, type Mode } from './modes';

export type Reason = 'full' | 'timeout' | null;

export interface GameState {
  readonly modeId: string;
  readonly size: number;
  /** Row-major grid; null = empty. */
  readonly cells: readonly (string | null)[];
  /** The full deterministic letter sequence (length = cell count). */
  readonly stream: readonly string[];
  /** Letters currently visible & placeable (length ≤ mode.queueSize). */
  readonly queue: readonly string[];
  /** Index into `stream` of the next letter to enter the queue. */
  readonly streamPos: number;
  /** How many letters have been placed. */
  readonly placed: number;
  readonly over: boolean;
  readonly reason: Reason;
  readonly timed: boolean;
  readonly roundMs: number;
  readonly msLeft: number;
}

export function createGame(mode: Mode, seed: number, timed = false): GameState {
  const stream = buildStream(mode, seed);
  const q = Math.min(mode.queueSize, stream.length);
  return {
    modeId: mode.id,
    size: mode.size,
    cells: Array<string | null>(cellCount(mode)).fill(null),
    stream,
    queue: stream.slice(0, q),
    streamPos: q,
    placed: 0,
    over: false,
    reason: null,
    timed,
    roundMs: mode.roundMs,
    msLeft: timed ? mode.roundMs : Infinity,
  };
}

/** The letters the player may place right now (1 in blind modes, up to N in Sluice). */
export function current(g: GameState): readonly string[] {
  return g.queue;
}

/**
 * Place queue letter `queueIdx` into empty cell `cellIdx`. Returns the same object if
 * the move is illegal (so callers can cheaply detect a no-op). The queue then refills
 * from the stream.
 */
export function place(g: GameState, cellIdx: number, queueIdx = 0): GameState {
  if (g.over) return g;
  if (cellIdx < 0 || cellIdx >= g.cells.length || g.cells[cellIdx] != null) return g;
  if (queueIdx < 0 || queueIdx >= g.queue.length) return g;

  const cells = g.cells.slice();
  cells[cellIdx] = g.queue[queueIdx];

  const queue = g.queue.slice();
  queue.splice(queueIdx, 1);
  let streamPos = g.streamPos;
  if (streamPos < g.stream.length) queue.push(g.stream[streamPos++]);

  const placed = g.placed + 1;
  const full = placed >= g.cells.length;
  return {
    ...g,
    cells,
    queue,
    streamPos,
    placed,
    over: full,
    reason: full ? 'full' : null,
  };
}

/** Advance the versus clock. Returns the same object if nothing changed. */
export function tickTimer(g: GameState, dtMs: number): GameState {
  if (!g.timed || g.over) return g;
  const msLeft = Math.max(0, g.msLeft - dtMs);
  if (msLeft <= 0) return { ...g, msLeft: 0, over: true, reason: 'timeout' };
  return { ...g, msLeft };
}

/** Convenience: the grid's current score + per-line breakdown. */
export function score(g: GameState, mode: Mode): GridScore {
  return scoreGrid(g.cells, mode);
}

/** Number of empty cells remaining. */
export function emptyCells(g: GameState): number[] {
  const out: number[] = [];
  for (let i = 0; i < g.cells.length; i++) if (g.cells[i] == null) out.push(i);
  return out;
}
